/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/no-floating-promises */
import {
  Col,
  Divider,
  type Form,
  List,
  message,
  Row,
  Collapse,
  Typography,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import Title from "antd/lib/typography/Title";
import { type ChangeEvent, useEffect, useState } from "react";
import React from "react";

// import ReactJson from '@textea/json-viewer'
import { JsonViewer, defineDataType } from "@textea/json-viewer";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import {
  changeMessageCollapse,
  selectFullMidservice,
  sendCommandAsync,
} from "../redux/slices/midservice/midserviceSlice";
import { type MessageType } from "../redux/slices/midservice/midserviceTypes";
import { v4 as uuidv4 } from "uuid";
import { type FieldData } from "../pages/MidservicePage/ConfigurationForm";
import { timestampToReadable, timestampToDatetime } from "../utils/time";
import {
  selectCanUserEdit,
  selectCurrentUser,
} from "../redux/slices/user/userSlice";
import { defaultParamsYaml } from "../constants/defaultMethodParameters";
import { getApiUrl } from "../utils/url";
import yaml from "js-yaml";
import SendCommandForm from "./SendCommandForm";
import { useForm } from "antd/lib/form/Form";

const { Panel } = Collapse;
const { Text } = Typography;

// const ClickMuncher = ({ children }: any) => {
//   return <div onClick={e => e.stopPropagation()}>{children}</div>;
// };

type ValidateStatus = Parameters<typeof Form.Item>[0]["validateStatus"];

const urlType = defineDataType<string>({
  is: (value) => {
    return typeof value === "string" && value.startsWith("http");
  },
  Component: (props) => {
    // eslint-disable-next-line react/prop-types
    const url = props.value;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          cursor: "pointer",
          color: "#1976d2",
          textDecoration: "underline",
          overflowWrap: "anywhere",
        }}
      >
        {url}
      </a>
    );
  },
});

const backendLinkType = defineDataType<string>({
  is(value) {
    return typeof value === "string" && value.startsWith("/api");
  },
  Component: (props) => {
    // eslint-disable-next-line react/prop-types
    const url = new URL(props.value, getApiUrl()).toString();
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          cursor: "pointer",
          color: "#1976d2",
          textDecoration: "underline",
          overflowWrap: "anywhere",
        }}
      >
        {url}
      </a>
    );
  },
});

const CustomJsonViewer = (value: any): JSX.Element => {
  return (
    <JsonViewer
      rootName={false}
      value={value}
      displaySize={false}
      quotesOnKeys={false}
      displayDataTypes={false}
      valueTypes={[urlType, backendLinkType]}
    ></JsonViewer>
  );
};

const urlFormatter = (
  val: MessageType,
  urlAttribute: string,
  nameAttribute: string = "name",
): JSX.Element => {
  const res = val.response?.result;
  const url: string | undefined = res?.[urlAttribute as keyof typeof res];
  const name: string | undefined = res?.[nameAttribute as keyof typeof res];
  if (url != null && name != null)
    return <a href={`${getApiUrl()}${url}`}>{name}</a>;
  throw new Error("formatter error");
};

const getlogFormatter = (val: MessageType): JSX.Element => {
  return urlFormatter(val, "archive_url");
};

const camshotFormatter = (val: MessageType): JSX.Element => {
  return urlFormatter(val, "image_url");
};

const getlogsFormatter = (val: MessageType): JSX.Element => {
  return urlFormatter(val, "archiveUrl");
};

const fileFormatter = (val: MessageType): JSX.Element => {
  return urlFormatter(val, "fileUrl");
};

const cmdFormatter = (val: MessageType): JSX.Element => {
  if (val.response.error === null) {
    const result = val.response.result;
    if (typeof result === "string") {
      return (
        <pre style={{ overflow: "auto", maxHeight: "30vh" }}>
          {result.trim() ? (
            result
          ) : (
            <span style={{ color: "gray", fontStyle: "italic" }}>
              (Пустой ответ)
            </span>
          )}
        </pre>
      );
    } else {
      return CustomJsonViewer(result);
    }
  }
  return CustomJsonViewer(val.response.error);
};

const formatters: Record<string, (val: MessageType) => JSX.Element> = {
  camshot: camshotFormatter,
  cmd: cmdFormatter,
  getlog: getlogFormatter,
  get_logs: getlogsFormatter,
  get_store: fileFormatter,
  upload: fileFormatter,
};

const formatResponse = (msg: MessageType): JSX.Element => {
  try {
    const formatter = formatters[msg.requestMethod];
    const res = formatter(msg);
    console.log("formatResponse res", res);
    return res;
  } catch {
    console.log("formatResponse catch");
    return CustomJsonViewer(msg.response);
  }
};

export const MidserviceMessagesTab = (): JSX.Element => {
  const fullInstance = useAppSelector(selectFullMidservice);
  const currentUser = useAppSelector(selectCurrentUser);
  const canUserEdit = useAppSelector(selectCanUserEdit);
  // const yaml = require("js-yaml");

  const [form] = useForm();

  const [methods, setMethods] = useState<any[]>([]);
  // const [currentMethod, setCurrentMethod] = useState<string>('')
  const dispatch = useAppDispatch();
  const [params, setParams] = useState<{
    value: string;
    validateStatus?: ValidateStatus;
    errorMsg?: string | null;
  }>({
    value: "",
  });

  const validateParams = (
    value: string,
  ): {
    validateStatus: ValidateStatus;
    errorMsg: string | null;
  } => {
    try {
      const yamlParsed = yaml.load(value);
      console.log("yaml load  params", yamlParsed);
      return {
        validateStatus: "success",
        errorMsg: null,
      };
    } catch (err) {
      return {
        validateStatus: "error",
        errorMsg: "Некорректный yaml",
      };
    }
  };

  const onParamsChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    const value = event.target.value;
    setParams({
      ...validateParams(value),
      value,
    });
  };

  useEffect(() => {
    if (fullInstance?.methods != null) {
      setMethods(
        fullInstance.methods.map((item) => {
          return { value: item };
        }),
      );
    }
  }, [fullInstance]);

  const onCollapseChange = (key: string): void => {
    console.log("onCollapseChange", key);
    dispatch(changeMessageCollapse(key));
  };

  const onFinish = (values: any): void => {
    if (fullInstance == null) return;
    try {
      const message: MessageType = {
        instanceId: fullInstance.midserviceInstance.id,
        callId: uuidv4(),
        requestMethod: values.method,
        requestParams: values.params != null ? yaml.load(values.params) : null,
        requestTime: Date.now(),
        authorId: currentUser.id,
        authorLogin: currentUser.login,
      };
      console.log("Success message:", message);
      const params = {
        deviceId: fullInstance.midserviceInstance.deviceId,
        domain: fullInstance.midserviceInstance.domain,
        message,
      };
      dispatch(sendCommandAsync(params));
    } catch (err) {
      console.log("err", err);
    }
  };

  const onFinishFailed = (errorInfo: any): void => {
    console.log("Failed:", errorInfo);
  };

  const getStatusStyles = (message: MessageType): any => {
    const styles: any = {};
    if (message?.response == null) return styles;
    if (message?.response?.result != null) styles.backgroundColor = "#d9f7be";
    else styles.backgroundColor = "#ffccc7";
    return styles;
  };

  const fillData = (e: any, message: MessageType): void => {
    e.stopPropagation();
    console.log("click", message);
    const paramsValue =
      message?.requestParams != null ? yaml.dump(message.requestParams) : "";
    const newFields: FieldData[] = [
      { name: "method", value: message.requestMethod },
      { name: "params", value: paramsValue },
    ];
    form.setFields(newFields);
  };

  const onMethodFielsChange = (value: string): void => {
    console.log("value", value);
    try {
      const defaultValue: string = defaultParamsYaml[value];
      console.log("default value", defaultValue);
      setParams({ ...params, value: defaultValue });
      console.log("click", message);
      const newFields: FieldData[] = [
        { name: "method", value },
        { name: "params", value: defaultValue },
      ];
      form.setFields(newFields);
    } catch (err) {}
  };

  console.log("params value", params.value);

  return (
    <>
      <Title level={5}>Вызов метода</Title>

      <SendCommandForm
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        params={params}
        onMethodFiedlsChange={onMethodFielsChange}
        onParamsChange={onParamsChange}
        methods={methods}
        canUserEdit={canUserEdit}
      />

      <Divider />
      <Title level={5}>История вызовов</Title>
      <List
        pagination={{
          onChange: (page) => {
            console.log(page);
          },
          pageSize: 10,
        }}
        dataSource={fullInstance?.messages}
        renderItem={(item) => (
          <List.Item style={{ margin: 0, padding: 0, display: "block" }}>
            <Collapse
              activeKey={item.isCollapseOpen ?? false ? item.callId : undefined}
              onChange={() => {
                onCollapseChange(item.callId);
              }}
            >
              <Panel
                style={getStatusStyles(item)}
                header={`${item.requestMethod}`}
                key={item.callId}
                extra={
                  <>
                    <Text type="secondary">
                      {item.authorLogin ?? "n/a"}
                      {" | "}
                      {timestampToDatetime(item.requestTime)}
                    </Text>

                    <EditOutlined
                      style={{ marginLeft: "10px" }}
                      onClick={(e) => {
                        fillData(e, item);
                      }}
                    />
                  </>
                }
              >
                <Row gutter={25}>
                  <Col span={12} style={{ borderRight: "1px solid #f5f5f5" }}>
                    <Row>
                      <Col flex="auto">
                        <Text type="secondary">Запрос</Text>
                      </Col>
                      <Col flex="none">
                        <Text type="secondary">
                          {timestampToReadable(item.requestTime)}
                        </Text>
                      </Col>
                    </Row>
                    <Row>
                      <CustomJsonViewer value={item.requestParams} />
                    </Row>
                  </Col>
                  <Col span={12}>
                    <Row>
                      <Col flex="auto">
                        <Text type="secondary">Ответ</Text>
                      </Col>
                      <Col flex="none">
                        <Text type="secondary">
                          {item.responseTime != null
                            ? timestampToReadable(item.responseTime)
                            : "Ожидание ..."}
                        </Text>
                      </Col>
                    </Row>

                    <Row style={{ whiteSpace: "pre-line" }}>
                      {item.response != null ? formatResponse(item) : <></>}
                    </Row>
                  </Col>
                </Row>
              </Panel>
            </Collapse>
          </List.Item>
        )}
      />
    </>
  );
};
